/**
 * pet-composite.js — shared pet-on-product compositing core.
 *
 * Pure rendering: given a base product mockup, an AI pet image, the template +
 * variant config, and an (optional) pet name, it draws the same composite the
 * instant-preview popup produces — base image → clip to print area → AI pet
 * (flat / bbox / cylindrical) → optional name text — and returns the canvas.
 *
 * No popup, no API, no editing, no DOM beyond the canvas it draws into. Used by
 * tile-personalizer.js to personalize product cards. (The instant-preview popup
 * still has its own copy of this math; consolidating the two onto this module is
 * a follow-up that needs PDP regression testing — see the tile-personalization
 * spec.)
 *
 * Exposed as window.PetComposite.
 */
(function () {
  "use strict";

  // Latin-subset woff2 URLs from Google Fonts. Mirror of InstantPreview.FONT_URLS
  // — when a new fontFamily appears in base_product_templates.default_name_config,
  // add it in both places.
  const FONT_URLS = {
    Montserrat:
      "https://fonts.gstatic.com/s/montserrat/v31/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCuM73w5aXp-p7K4KLg.woff2",
    "Alex Brush":
      "https://fonts.gstatic.com/s/alexbrush/v23/SZc83FzrJKuqFbwMKk6EhUXz7RlNiCY.woff2",
    Calistoga:
      "https://fonts.gstatic.com/s/calistoga/v18/6NUU8F2OJg6MeR7l4e0fs8wB49dJfg.woff2",
  };

  // Visual aliases — when Supabase says X, render as Y. Mirror of
  // InstantPreview.FONT_ALIAS.
  const FONT_ALIAS = {
    Anton: { family: "Montserrat", weight: 700 },
  };

  // Cylindrical warp strength for mug previews. Mirror of
  // InstantPreview.MUG_CYLINDER_CURVATURE.
  const MUG_CYLINDER_CURVATURE = 0.5;

  // url -> Promise<HTMLImageElement>. Lets the same AI image (or base image) be
  // decoded once and reused across every tile.
  const imageCache = new Map();

  function resolveFont(family, weight) {
    const alias = FONT_ALIAS[family];
    return alias
      ? { family: alias.family, weight: alias.weight }
      : { family, weight };
  }

  async function ensureFontLoaded(family) {
    if (!family || typeof document === "undefined" || !document.fonts) return;
    const { family: resolved } = resolveFont(family);
    const already = Array.from(document.fonts).find(
      (f) => f.family.replace(/^"|"$/g, "") === resolved && f.status === "loaded",
    );
    if (already) return;
    const url = FONT_URLS[resolved];
    if (!url) {
      console.warn(
        `[pet-composite] no woff2 URL registered for "${resolved}" — canvas will use sans-serif fallback.`,
      );
      return;
    }
    try {
      const face = new FontFace(resolved, `url(${url})`);
      document.fonts.add(face);
      await face.load();
    } catch (err) {
      console.warn("[pet-composite] font load failed for", resolved, err);
    }
  }

  function loadImage(url) {
    if (imageCache.has(url)) return imageCache.get(url);
    const p = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => {
        // Drop the failed entry so a later retry can re-attempt.
        imageCache.delete(url);
        const err = new Error(`Failed to load: ${url}`);
        err.code = "IMAGE_LOAD_FAILED";
        err.url = url;
        reject(err);
      };
      img.src = url;
    });
    imageCache.set(url, p);
    return p;
  }

  function tracePrintAreaPath(ctx, pa, borderRadiusPct) {
    const pct = borderRadiusPct ?? 0;
    const r = Math.min(pa.width, pa.height) * (Math.max(0, Math.min(50, pct)) / 100);
    ctx.beginPath();
    if (r === 0) {
      ctx.rect(pa.x, pa.y, pa.width, pa.height);
      return;
    }
    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(pa.x, pa.y, pa.width, pa.height, r);
      return;
    }
    const { x, y, width: w, height: h } = pa;
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function drawTextWithSpacing(ctx, text, x, y, spacing) {
    const chars = text.split("");
    const totalWidth = chars.reduce(
      (w, c) => w + ctx.measureText(c).width + spacing,
      -spacing,
    );
    let cx = x - totalWidth / 2;
    for (const char of chars) {
      const cw = ctx.measureText(char).width;
      ctx.fillText(char, cx + cw / 2, y);
      cx += cw + spacing;
    }
  }

  function isCylindrical(template) {
    return (
      template?.print_area_topology === "non-flat" &&
      (template?.slug || "").startsWith("mug-")
    );
  }

  // Per-column cylindrical warp — identical model to instant-preview's
  // warpCylindrical. See that file for the full derivation comments.
  function warpCylindrical(ctx, image, bbox, curvature) {
    if (!curvature) {
      ctx.drawImage(image, bbox.x, bbox.y, bbox.width, bbox.height);
      return;
    }
    const thetaMax = Math.max(0.001, Math.min(1, curvature)) * (Math.PI / 2);
    const sinThetaMax = Math.sin(thetaMax);
    const cosThetaMax = Math.cos(thetaMax);
    const sliceCount = Math.max(80, Math.min(400, Math.floor(bbox.width)));
    const sliceWidth = bbox.width / sliceCount;
    const sw = image.naturalWidth;
    const sh = image.naturalHeight;
    const srcSliceWidth = sw / sliceCount;
    const bowFracTop = 0.03;
    const bowFracBottom = 0.06;
    const rimShadowMax = 0.13;

    const bowPad = Math.ceil(bowFracTop * bbox.height);
    const off = document.createElement("canvas");
    off.width = Math.ceil(bbox.width);
    off.height = Math.ceil(bbox.height) + bowPad;
    const offCtx = off.getContext("2d");

    for (let i = 0; i < sliceCount; i++) {
      const f = (i + 0.5) / sliceCount;
      const xNorm = (f - 0.5) * 2;
      const sinTheta = Math.max(-1, Math.min(1, xNorm * sinThetaMax));
      const theta = Math.asin(sinTheta);
      const sourceF = (theta + thetaMax) / (2 * thetaMax);
      const sx = sourceF * sw - srcSliceWidth / 2;
      const sxClamped = Math.max(0, Math.min(sw - srcSliceWidth, sx));
      const dx = i * sliceWidth;
      const bowNorm = (1 - Math.cos(theta)) / (1 - cosThetaMax);
      const topShift = bowFracTop * bbox.height * bowNorm;
      const bottomShift = bowFracBottom * bbox.height * bowNorm;
      const dyOffset = bowPad - topShift;
      const destHeight = bbox.height - (bottomShift - topShift);
      offCtx.drawImage(
        image,
        sxClamped, 0, srcSliceWidth, sh,
        dx - 0.5, dyOffset, sliceWidth + 1, destHeight,
      );
    }

    if (rimShadowMax > 0) {
      const grad = offCtx.createLinearGradient(0, 0, off.width, 0);
      grad.addColorStop(0, `rgba(0,0,0,${rimShadowMax})`);
      grad.addColorStop(0.18, `rgba(0,0,0,${rimShadowMax * 0.35})`);
      grad.addColorStop(0.5, `rgba(0,0,0,0)`);
      grad.addColorStop(0.82, `rgba(0,0,0,${rimShadowMax * 0.35})`);
      grad.addColorStop(1, `rgba(0,0,0,${rimShadowMax})`);
      offCtx.globalCompositeOperation = "source-atop";
      offCtx.fillStyle = grad;
      offCtx.fillRect(0, 0, off.width, off.height);
    }

    ctx.drawImage(off, bbox.x, bbox.y - bowPad);
  }

  // Pet placement in canvas pixels — bbox path (non-flat, pipeline-populated)
  // or legacy fractional-of-print-area path (flat).
  function computePetPlacement(template, printArea, aiImage) {
    const bbox = template.default_pet_placement_bbox;
    if (bbox) {
      const aspect = aiImage.naturalHeight / aiImage.naturalWidth;
      const petW = bbox.width;
      const petH = petW * aspect;
      return {
        x: bbox.x,
        y: bbox.y + (bbox.height - petH) / 2,
        width: petW,
        height: petH,
      };
    }
    const pa = printArea;
    const tp = template.default_pet_placement;
    const petW = pa.width * tp.scale;
    const petH = (petW / aiImage.naturalWidth) * aiImage.naturalHeight;
    return {
      x: pa.x + pa.width * tp.x - petW / 2,
      y: pa.y + pa.height * tp.y - petH / 2,
      width: petW,
      height: petH,
    };
  }

  // Whether a canvas-rendered name layer applies. AI-baked names
  // (needpetname=true) and templates without a name config skip it.
  function wantsName(template, needpetname) {
    const aiBaked = needpetname === true || needpetname === "true";
    return !!(template.has_pet_name && template.default_name_config && !aiBaked);
  }

  function computeNamePlacement(template, printArea, petName) {
    const nc = template.default_name_config;
    const name = (petName || "").trim();
    const nameBbox = template.default_name_bbox;
    if (nameBbox) {
      return {
        x: nameBbox.anchor.x,
        y: nameBbox.anchor.y,
        fontSize: nameBbox.fontSizeMockupPx,
        text: name,
        fontFamily: nc?.fontFamily || "Anton",
        fontWeight: nc?.fontWeight ?? 400,
        fontColor: nc?.fontColor || "#000000",
        letterSpacing: nc?.letterSpacing ?? 0,
      };
    }
    const pa = printArea;
    return {
      x: pa.x + pa.width * nc.x,
      y: pa.y + pa.height * nc.y,
      fontSize: (nc.fontSize ?? 200) * (nc.scale ?? 1),
      text: name,
      fontFamily: nc.fontFamily || "Anton",
      fontWeight: nc.fontWeight ?? 400,
      fontColor: nc.fontColor || "#000000",
      letterSpacing: nc.letterSpacing ?? 0,
    };
  }

  /**
   * Synchronous render. Images and fonts MUST already be loaded.
   *   canvas  — target <canvas> (resized to the base image's native size)
   *   opts.baseImage / opts.aiImage — decoded HTMLImageElements
   *   opts.template / opts.printArea — config (see /api/preview/tiles-config)
   *   opts.petName / opts.needpetname
   */
  function render(canvas, opts) {
    const { baseImage, aiImage, template, printArea, petName, needpetname } = opts;
    const ctx = canvas.getContext("2d");
    const size = baseImage.naturalWidth;
    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(baseImage, 0, 0, size, size);

    ctx.save();
    tracePrintAreaPath(ctx, printArea, template.print_area_border_radius_pct);
    ctx.clip();

    if (aiImage) {
      const pet = computePetPlacement(template, printArea, aiImage);
      if (isCylindrical(template)) {
        warpCylindrical(ctx, aiImage, pet, MUG_CYLINDER_CURVATURE);
      } else {
        ctx.drawImage(aiImage, pet.x, pet.y, pet.width, pet.height);
      }
    }

    if (wantsName(template, needpetname)) {
      const n = computeNamePlacement(template, printArea, petName);
      if (n.text) {
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const { family: renderFamily, weight: renderWeight } = resolveFont(
          n.fontFamily,
          n.fontWeight || 400,
        );
        ctx.fillStyle = n.fontColor;
        ctx.font = `${renderWeight} ${n.fontSize}px "${renderFamily}", sans-serif`;
        const isAnton = n.fontFamily === "Anton";
        const text = isAnton ? n.text.toUpperCase() : n.text;
        if (isAnton && n.letterSpacing) {
          drawTextWithSpacing(ctx, text, n.x, n.y, n.letterSpacing);
        } else {
          ctx.fillText(text, n.x, n.y);
        }
      }
    }

    ctx.restore();
    return canvas;
  }

  /**
   * Load (cached) + render in one call. Returns the drawn canvas.
   *   opts.baseImageUrl, opts.aiImageUrl  — required
   *   opts.template, opts.printArea       — config
   *   opts.petName, opts.needpetname
   *   opts.canvas                         — optional reuse target
   */
  async function composite(opts) {
    const [baseImage, aiImage] = await Promise.all([
      loadImage(opts.baseImageUrl),
      loadImage(opts.aiImageUrl),
    ]);
    if (wantsName(opts.template, opts.needpetname)) {
      await ensureFontLoaded(opts.template.default_name_config?.fontFamily);
    }
    const canvas = opts.canvas || document.createElement("canvas");
    return render(canvas, {
      baseImage,
      aiImage,
      template: opts.template,
      printArea: opts.printArea,
      petName: opts.petName,
      needpetname: opts.needpetname,
    });
  }

  window.PetComposite = {
    loadImage,
    render,
    composite,
    ensureFontLoaded,
    _internals: { computePetPlacement, computeNamePlacement, wantsName },
  };
})();
